import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteProduct, getProductBySlug, getProductStockHistory } from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";
import { FaEye } from "react-icons/fa";
import StatusBadge from "../Shared/StatusBadge";
import RecordActions from "../Shared/RecordActions";
import ActionIconButton from "../Shared/ActionIconButton";
import FullPageLoader from "../../common/FullPageLoader";

export default function AdminProductView() {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const { slug } = useParams();
  const [variants, setVariants] = useState([]);
  const [stockHistoryVariant, setStockHistoryVariant] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockHistoryError, setStockHistoryError] = useState("");

  const loadStockHistory = useCallback(
    async (variant) => {
      if (!product?._id || !variant?._id) {
        setStockHistoryVariant(variant);
        setStockHistory([]);
        setStockHistoryError("Product or variant ID is unavailable.");
        return;
      }

      setStockHistoryVariant(variant);
      setStockHistory([]);
      setStockHistoryError("");
      setStockHistoryLoading(true);

      try {
        const response = await getProductStockHistory(product._id, variant._id);
        const payload = response?.data || response;
        const history =
          (Array.isArray(payload) && payload) ||
          (Array.isArray(payload?.movements) && payload.movements) ||
          (Array.isArray(payload?.history) && payload.history) ||
          (Array.isArray(payload?.stockHistory) && payload.stockHistory) ||
          (Array.isArray(payload?.data) && payload.data) ||
          [];
        setStockHistory(history);
      } catch (error) {
        setStockHistoryError(getErrorMessage(error));
      } finally {
        setStockHistoryLoading(false);
      }
    },
    [product?._id],
  );

  const handleStockHistory = async (variant) => {
    if (stockHistoryVariant?._id === variant?._id) {
      setStockHistoryVariant(null);
      setStockHistory([]);
      setStockHistoryError("");
      return;
    }

    await loadStockHistory(variant);
  };

  useEffect(() => {
    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  useEffect(() => {
    const onlyVariant = variants[0];
    if (
      !loading &&
      variants.length === 1 &&
      product?._id &&
      stockHistoryVariant?._id !== onlyVariant?._id
    ) {
      loadStockHistory(onlyVariant);
    }
  }, [loading, variants, product?._id, stockHistoryVariant?._id, loadStockHistory]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setStockHistoryVariant(null);
      setStockHistory([]);
      setStockHistoryError("");

      const response = await getProductBySlug(slug);
      setFeaturedImageUrl(response?.data?.featuredImageUrl);
      const productVariants = Array.isArray(response?.data?.variants) ? response.data.variants : [];
      setVariants(productVariants);
      setProduct(response?.data?.product || response?.data);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <FullPageLoader text="Loading product..." />;
  }

  if (!product) {
    return (
      <div className="container-fluid p-5 text-center">
        <h5>Product Not Found</h5>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!product?._id || !window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    try {
      await deleteProduct(product._id);
      alert("Product deleted successfully");
      navigate("/products");
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const getHistoryValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    if (typeof value === "object") {
      return value.name || value.value || value.email || "-";
    }
    return value;
  };

  return (
    <div className="container-fluid p-4">
      <RecordActions
        onBack={() => navigate("/products")}
        onEdit={() =>
          navigate(`/product/edit/${product?.seo?.slug || slug}`, {
            state: { productId: product._id, slug: product?.seo?.slug || slug },
          })
        }
        onDelete={handleDelete}
      />

      <div className="row">
        {/* LEFT SIDE */}
        <div className="col-lg-5">
          {/* FEATURED IMAGE */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <img
                src={product?.seo?.metaImageUrl || "/default-product.png"}
                alt={product?.productName}
                className="w-100 rounded"
                style={{
                  height: "400px",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>

          {/* GALLERY */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">Gallery Images</h5>

              <div className="row">
                {product?.imageUrls?.length > 0 ? (
                  product?.imageUrls?.map((img, index) => (
                    <div className="col-4 mb-3" key={index}>
                      <img
                        src={img}
                        alt="gallery"
                        className="w-100 rounded border"
                        style={{
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <p>No Images</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              {/* PRODUCT NAME */}
              <h3 className="mb-2">{product?.productName || "-"}</h3>

              {/* BADGES */}
              <div className="mb-3 d-flex flex-wrap gap-2">
                {product?.badges?.map((badge, index) => (
                  <span
                    key={index}
                    style={{
                      padding: "6px 16px",
                      fontSize: "12px",
                      borderRadius: "50px",
                      border: "1px solid #28a745",
                      fontWeight: "500",
                    }}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* PRICE */}
              <div className="mb-4">
                <h3 className="text-success mb-1">₹{variants[0]?.price || 0}</h3>
              </div>

              {/* DETAILS */}
              <div className="table-responsive">
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <th>Preview</th>
                      <td
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          fontFamily: "inherit",
                          lineHeight: "1.6",
                        }}
                      >
                        {product?.preview?.trim() || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Slug</th>
                      <td>{product?.seo?.slug}</td>
                    </tr>

                    <tr>
                      <th>Category</th>
                      <td>{product?.category}</td>
                    </tr>

                    <tr>
                      <th>SKU</th>
                      <td>{product?.sku}</td>
                    </tr>

                    <tr>
                      <th>Stock</th>
                      <td>{product?.maxQuantity}</td>
                    </tr>

                    <tr>
                      <th>Featured</th>
                      <td>{product?.isFeatured ? "Yes" : "No"}</td>
                    </tr>

                    <tr>
                      <th>Status</th>
                      <td>
                        <StatusBadge status={product?.status} />
                      </td>
                    </tr>

                    <tr>
                      <th>In Stock</th>
                      <td>{product?.inStock ? "Available" : "Out Of Stock"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION */}
              <div className="mt-4">
                <h5>Description</h5>

                <p className="text-muted">{product?.productDescription}</p>
              </div>

              {/* VARIANTS */}
              <div className="mt-5">
                <h4 className="mb-4">Variants</h4>

                {variants.length > 0 ? (
                  <div className="row">
                    {variants.map((variant, index) => (
                      <div className="col-md-6 mb-4" key={variant?._id || index}>
                        <div className="card border shadow-sm">
                          <div className="p-2">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <h5 className="mb-0">Variant #{index + 1}</h5>
                              {variants.length > 1 && (
                                <ActionIconButton
                                  icon={<FaEye />}
                                  label={
                                    stockHistoryVariant?._id === variant?._id
                                      ? "Hide Stock History"
                                      : "View Stock History"
                                  }
                                  variant="view"
                                  onClick={() => handleStockHistory(variant)}
                                  loading={
                                    stockHistoryLoading &&
                                    stockHistoryVariant?._id === variant?._id
                                  }
                                />
                              )}
                            </div>

                            <p className="mb-0">
                              <strong className="text-sm">Font:</strong>
                              {variant?.fontId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Size:</strong>
                              {variant?.sizeId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Material:</strong>

                              {variant?.materialId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Color:</strong>
                              {variant?.colorId?.name || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Price:</strong>₹{variant?.price || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No Variants Available</p>
                )}
              </div>
            </div>
          </div>
        </div>
        {stockHistoryVariant && (
          <div className="card border-0 shadow-sm mt-2">
            <div className="card-body">
              <h5 className="mb-3">
                Stock History
                {stockHistoryVariant?.sku ? ` - ${stockHistoryVariant.sku}` : ""}
              </h5>

              {stockHistoryLoading ? (
                <p className="text-muted mb-0">Loading stock history...</p>
              ) : stockHistoryError ? (
                <div className="alert alert-danger mb-0" role="alert">
                  {stockHistoryError}
                </div>
              ) : stockHistory.length === 0 ? (
                <p className="text-muted mb-0">No stock history available.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Direction</th>
                        <th>Quantity</th>
                        <th>Balance After</th>
                        <th>Reason</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockHistory.map((entry, index) => (
                        <tr key={entry?._id || entry?.id || index}>
                          <td>
                            {getHistoryValue(entry?.createdAt || entry?.date || entry?.updatedAt)}
                          </td>
                          <td>{getHistoryValue(entry?.direction || entry?.type)}</td>
                          <td>{getHistoryValue(entry?.quantity ?? entry?.quantityChanged)}</td>
                          <td>
                            {getHistoryValue(
                              entry?.balanceAfter ?? entry?.currentStock ?? entry?.remainingStock,
                            )}
                          </td>
                          <td>{getHistoryValue(entry?.reason)}</td>
                          <td>{getHistoryValue(entry?.note)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
